// Shared identities and display names; no agronomic model is inferred from a name.
export const ADDITIONAL_CROP_IDS = Object.freeze([
  'potato',
  'tobacco',
  'sugarcane',
  'lemon',
  'avocado',
  'orange',
])

export const CROP_LABELS = Object.freeze({
  maize: {es: 'Maíz', en: 'Maize'},
  soybean: {es: 'Soja', en: 'Soybean'},
  wheat: {es: 'Trigo', en: 'Wheat'},
  peanut: {es: 'Maní', en: 'Peanut'},
  potato: {es: 'Papa', en: 'Potato'},
  tobacco: {es: 'Tabaco', en: 'Tobacco'},
  sugarcane: {es: 'Caña de azúcar', en: 'Sugarcane'},
  lemon: {es: 'Limón', en: 'Lemon'},
  avocado: {es: 'Palta', en: 'Avocado'},
  orange: {es: 'Naranja', en: 'Orange'},
})

export const CROP_CATALOG = Object.freeze(
  [
    {id: 'maize', economicId: 'corn', aliases: ['corn', 'maíz', 'maiz'], maximumAnalysisMonths: 7},
    {id: 'soybean', economicId: 'soybean', aliases: ['soy', 'soja'], maximumAnalysisMonths: 7},
    {id: 'wheat', economicId: 'wheat', aliases: ['trigo'], maximumAnalysisMonths: 7},
    {id: 'peanut', economicId: 'peanut', aliases: ['maní', 'mani'], maximumAnalysisMonths: 7},
    {id: 'potato', economicId: 'potato', aliases: ['papa', 'patata'], maximumAnalysisMonths: 7},
    {id: 'tobacco', economicId: 'tobacco', aliases: ['tabaco'], maximumAnalysisMonths: 7},
    {
      id: 'sugarcane',
      economicId: 'sugarcane',
      aliases: ['caña de azúcar', 'cana de azucar', 'sugar cane', 'sugar_cane'],
      maximumAnalysisMonths: 24,
    },
    {id: 'lemon', economicId: 'lemon', aliases: ['limón', 'limon'], maximumAnalysisMonths: 12},
    {
      id: 'avocado',
      economicId: 'avocado',
      aliases: ['palta', 'aguacate'],
      maximumAnalysisMonths: 12,
    },
    {id: 'orange', economicId: 'orange', aliases: ['naranja'], maximumAnalysisMonths: 12},
  ].map((crop) =>
    Object.freeze({
      ...crop,
      labels: CROP_LABELS[crop.id],
      phenologySupported: !ADDITIONAL_CROP_IDS.includes(crop.id),
    })
  )
)

export const AGRONOMIC_CROP_IDS = Object.freeze(CROP_CATALOG.map((crop) => crop.id))

const aliasKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s]+/g, ' ')

export const getCropDefinition = (value) => {
  const key = aliasKey(value)
  return (
    CROP_CATALOG.find((crop) =>
      [crop.id, crop.economicId, ...crop.aliases].some((alias) => aliasKey(alias) === key)
    ) || null
  )
}

export const normalizeCropId = (value) => getCropDefinition(value)?.id || String(value || '').trim()

export const getCropLabel = (value, locale = 'es') => {
  const crop = getCropDefinition(value)
  return (
    crop?.labels[
      String(locale || '')
        .toLowerCase()
        .startsWith('en')
        ? 'en'
        : 'es'
    ] ||
    String(value || '').trim() ||
    '-'
  )
}

export const isObservationalCrop = (value) =>
  ADDITIONAL_CROP_IDS.includes(getCropDefinition(value)?.id)

// Bounded observation windows, not crop-cycle or maturity estimates.
export const getCropAnalysisMonths = (value) => getCropDefinition(value)?.maximumAnalysisMonths || 7

export const getObservationalProfileId = (value) =>
  isObservationalCrop(value) ? `${normalizeCropId(value)}_observational` : ''
